import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faQuestion } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import PageShell from './layout/PageShell';
import PageHeader from './layout/PageHeader';
import Panel from './layout/Panel';
import './layout/layout.css';

interface ToolLayoutProps {
  title: string;
  description?: string;
  icon?: IconDefinition;

  /** Options row above the panes: mode toggles, example loaders, checkboxes. */
  controls?: React.ReactNode;

  error?: string | null;
  onDismissError?: () => void;

  /**
   * Custom input body, mirroring `outputContent`. When set, the textarea props
   * below are ignored — used by the tools whose input is a small form (the
   * UUID generator's options, the regex tester's pattern + flags) rather than
   * one block of text.
   */
  inputContent?: React.ReactNode;

  /* input pane */
  inputLabel?: React.ReactNode;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  inputPlaceholder?: string;
  inputRows?: number;
  inputReadOnly?: boolean;
  /** Small hint rendered under the input control. */
  inputHint?: React.ReactNode;

  /** Action column between the panes. Omit for a two-pane tool. */
  actions?: React.ReactNode;

  /** Custom output body. When set, the textarea props below are ignored. */
  outputContent?: React.ReactNode;

  /* textarea output pane */
  outputLabel?: React.ReactNode;
  outputValue?: string;
  outputPlaceholder?: string;
  outputRows?: number;
  outputReadOnly?: boolean;

  /** Copy-to-clipboard affordance for the textarea output. */
  onCopy?: () => void;
  copied?: boolean;

  aboutTitle?: string;
  about?: React.ReactNode;

  /** Long-form content rendered under the about panel (see ToolContent). */
  children?: React.ReactNode;
}

const HELP_ID = 'tool-help';

/**
 * The one layout every text tool shares: page header, options row, error slot,
 * an input pane, an action column and an output pane. Panes sit side by side
 * from 768px up and stack below (see layout.css); no tool page writes Row/Col
 * layout code or its own responsive rules.
 *
 * The explanatory copy (`about` + `children`) is collapsed behind the `?` in
 * the header: someone who followed a link to a tool sees the input, not an
 * essay. It is rendered but `hidden` rather than unmounted, so crawlers that
 * execute JavaScript still read it and in-page search still finds it.
 */
const ToolLayout: React.FC<ToolLayoutProps> = ({
  title,
  description,
  icon,
  controls,
  error,
  onDismissError,
  inputContent,
  inputLabel,
  inputValue = '',
  onInputChange,
  inputPlaceholder,
  inputRows = 10,
  inputReadOnly = false,
  inputHint,
  actions,
  outputContent,
  outputLabel,
  outputValue = '',
  outputPlaceholder = 'Output will appear here...',
  outputRows = 10,
  outputReadOnly = true,
  onCopy,
  copied = false,
  aboutTitle,
  about,
  children,
}) => {
  const hasActions = Boolean(actions);
  const hasHelp = Boolean(about || children);
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <PageShell width="narrow">
      <PageHeader
        className="page-head--tool"
        icon={icon}
        title={title}
        subtitle={description}
        actions={
          hasHelp ? (
            <Button
              variant={helpOpen ? 'secondary' : 'outline-secondary'}
              className="tool-help__toggle"
              onClick={() => setHelpOpen((open) => !open)}
              aria-expanded={helpOpen}
              aria-controls={HELP_ID}
              aria-label={helpOpen ? 'Hide how this tool works' : 'How this tool works'}
              title={helpOpen ? 'Hide how this tool works' : 'How this tool works'}
            >
              <FontAwesomeIcon icon={faQuestion} aria-hidden="true" />
            </Button>
          ) : undefined
        }
      />

      <Panel>
        {controls && <div className="tool-controls">{controls}</div>}

        {error && (
          <Alert variant="danger" dismissible onClose={onDismissError}>
            {error}
          </Alert>
        )}

        <div className={`tool-panes ${hasActions ? '' : 'tool-panes--no-actions'}`.trim()}>
          <div className="tool-panes__input">
            {inputContent ?? (
              <Form.Group>
                {inputLabel && (
                  <Form.Label>
                    <strong>{inputLabel}</strong>
                  </Form.Label>
                )}
                <Form.Control
                  as="textarea"
                  rows={inputRows}
                  value={inputValue}
                  onChange={(e) => onInputChange?.(e.target.value)}
                  placeholder={inputPlaceholder}
                  readOnly={inputReadOnly}
                  className="font-monospace"
                />
                {inputHint && <Form.Text className="text-muted">{inputHint}</Form.Text>}
              </Form.Group>
            )}
          </div>

          {hasActions && <div className="tool-panes__actions">{actions}</div>}

          <div className="tool-panes__output">
            {outputContent ?? (
              <>
                <Form.Group>
                  {outputLabel && (
                    <Form.Label>
                      <strong>{outputLabel}</strong>
                    </Form.Label>
                  )}
                  <Form.Control
                    as="textarea"
                    rows={outputRows}
                    value={outputValue}
                    readOnly={outputReadOnly}
                    placeholder={outputPlaceholder}
                    className="font-monospace bg-light"
                  />
                </Form.Group>
                {outputValue && onCopy && (
                  <Button variant="success" size="sm" onClick={onCopy} className="mt-2">
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-2" />
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Panel>

      {hasHelp && (
        <div id={HELP_ID} className="tool-help" hidden={!helpOpen}>
          {about && (
            <Panel variant="quiet" title={aboutTitle} className="mt-4">
              <div className="tool-about">{about}</div>
            </Panel>
          )}

          {children}
        </div>
      )}
    </PageShell>
  );
};

export default ToolLayout;
